/**
 * Produk Flow (PLAN_FLOW_PRODUK.md Fasa 1, 30 Sept 2026) — admin tambah / rename /
 * tukar harga, tempoh dan had credit di page Products.
 *
 * Koleksi `flow_products/{id}`:
 *   { name, jenis: 1|2, priceSen, days, hadKredit (Type 2), onBillplz, active, order,
 *     createdAt, updatedAt, updatedBy }
 *
 * ⚠️ KEMBAR bentuk sah: produkSah() dalam functions/flowLangganan.js + firestore.rules
 *    (flow_products). Server Billplz membaca produk yang `onBillplz` (Type 1, satu
 *    sahaja buat masa ini) — ubah peraturan di sini → semak dua tempat itu.
 * Tiada padam: produk yang pernah dijual dirujuk oleh resit + langganan lama.
 *   Guna "active: false" untuk berhenti menjual.
 */
import { db, auth } from '../firebase';
import { collection, getDocs, doc, serverTimestamp, writeBatch } from 'firebase/firestore';

const PRODUCTS = 'flow_products';

export async function muatProdukFlow() {
    const snap = await getDocs(collection(db, PRODUCTS));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || String(a.name).localeCompare(String(b.name)));
}

/** Semak borang. Pulang mesej ralat (BI, UI admin) atau '' kalau sah. */
export function semakProduk(p) {
    const nama = String(p.name || '').trim();
    if (nama.length < 2 || nama.length > 80) return 'Enter a product name (2–80 characters).';
    if (p.jenis !== 1 && p.jenis !== 2) return 'Pick Type 1 or Type 2.';
    if (!Number.isSafeInteger(p.priceSen) || p.priceSen < 100 || p.priceSen > 1000000) return 'Price must be between RM1 and RM10,000.';
    if (!Number.isSafeInteger(p.days) || p.days < 1 || p.days > 365) return 'Duration must be 1–365 days.';
    if (p.jenis === 2 && (!Number.isSafeInteger(p.hadKredit) || p.hadKredit <= 0 || p.hadKredit > 1000000)) {
        return 'Type 2 needs a credit limit.';
    }
    if (p.onBillplz && p.jenis !== 1) return 'Only a Type 1 product can be sold on Billplz for now.';
    if (p.onBillplz && !p.active) return 'An inactive product cannot be sold on Billplz.';
    return '';
}

/**
 * Simpan produk (baru bila tiada id). Bila produk ini ditanda onBillplz, produk
 * lain yang ditanda DIBUANG tandanya dalam batch yang sama — Billplz jual SATU
 * produk sahaja (Fasa 1), jadi tiada keadaan dua produk berebut.
 */
export async function simpanProduk(p, semua) {
    const ralat = semakProduk(p);
    if (ralat) throw new Error(ralat);
    const data = {
        name: String(p.name).trim(),
        jenis: p.jenis,
        priceSen: p.priceSen,
        days: p.days,
        hadKredit: p.jenis === 2 ? p.hadKredit : null,
        onBillplz: !!p.onBillplz,
        active: !!p.active,
        order: Number.isSafeInteger(p.order) ? p.order : (semua.length + 1),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || '',
    };
    const batch = writeBatch(db);
    const ref = p.id ? doc(db, PRODUCTS, p.id) : doc(collection(db, PRODUCTS));
    if (p.id) batch.update(ref, data);
    else batch.set(ref, { ...data, createdAt: serverTimestamp() });
    if (data.onBillplz) {
        semua.filter((x) => x.id !== ref.id && x.onBillplz)
            .forEach((x) => batch.update(doc(db, PRODUCTS, x.id), { onBillplz: false, updatedAt: serverTimestamp() }));
    }
    await batch.commit();
    return ref.id;
}

/**
 * Produk bagi satu langganan. Rekod lama tiada productId → diteka ikut type:
 * Type 2 → produk Type 2 pertama; Type 1 → produk Billplz, atau Type 1 pertama.
 * PAPARAN sahaja — tiada apa ditulis.
 */
export function produkUntuk(c, produk) {
    if (c.productId) {
        const p = produk.find((x) => x.id === c.productId);
        if (p) return p;
    }
    const jenis = c.jenis === 2 ? 2 : 1;
    return (jenis === 1 && produk.find((x) => x.onBillplz && x.jenis === 1))
        || produk.find((x) => x.jenis === jenis) || null;
}

export const rmDariSen = (sen) => `RM${(sen / 100).toLocaleString('en-MY', { minimumFractionDigits: sen % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;

// Untuk borang: "RM100" / "100.50" → sen (integer) atau NaN.
export function senDariTeks(t) {
    const n = Number(String(t).replace(/[^\d.]/g, ''));
    return Number.isFinite(n) && String(t).trim() !== '' ? Math.round(n * 100) : NaN;
}

